import React, { useState, useEffect } from 'react';
import { websiteAnalyticsService } from '../../services/websiteAnalyticsService';
import { WebsiteAnalyticsFilters } from '../../types/websiteAnalytics';
import BarChart from '../Charts/BarChart';
import PieChart from '../Charts/PieChart';

interface ButtonClickAnalyticsProps {
  filters?: WebsiteAnalyticsFilters;
  refreshInterval?: number;
}

interface ApiButtonClick {
  page: string;
  element: string;
  analyticsName?: string;
  clicks: number;
  uniqueUsers: number;
}

interface ButtonClickStats {
  buttonText: string;
  buttonId: string;
  page: string;
  clicks: number;
  category: string;
  uniqueSessions: number;
}

export const ButtonClickAnalytics: React.FC<ButtonClickAnalyticsProps> = ({
  filters,
  refreshInterval = 300000
}) => {
  const [buttonClicks, setButtonClicks] = useState<ApiButtonClick[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPage, setSelectedPage] = useState<string>('all');

  const fetchButtonClicks = async () => {
    try {
      setLoading(true);
      const response = await websiteAnalyticsService.getButtonClicks(filters);
      
      if (response.success && response.data) {
        const clicksData = Array.isArray(response.data) 
          ? response.data 
          : (response.data as any).data || [];
        setButtonClicks(clicksData);
        setError(null);
      } else {
        setError('Failed to fetch button click data');
        setButtonClicks([]);
      }
    } catch (err) {
      setError('Error loading button click analytics');
      console.error('Error fetching button clicks:', err);
      setButtonClicks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchButtonClicks();
    const interval = setInterval(fetchButtonClicks, refreshInterval);
    return () => clearInterval(interval);
  }, [filters, refreshInterval]);

  const processButtonClickData = (): ButtonClickStats[] => {
    const filteredClicks = selectedPage === 'all' 
      ? buttonClicks 
      : buttonClicks.filter(click => click.page === selectedPage);

    return filteredClicks.map(click => ({
      // Prefer analyticsName over element (as recommended)
      buttonText: click.analyticsName || click.element || 'Unknown',
      buttonId: click.element || 'unknown',
      page: click.page || '/',
      clicks: click.clicks || 0,
      category: guessCategory(click.analyticsName || click.element),
      uniqueSessions: click.uniqueUsers || 0
    })).sort((a, b) => b.clicks - a.clicks);
  };

  const guessCategory = (element: string): string => {
    if (!element) return 'other';
    const lower = element.toLowerCase();
    if (lower.includes('nav') || lower.includes('menu') || lower.includes('link')) return 'navigation';
    if (lower.includes('book') || lower.includes('submit') || lower.includes('cta') || lower.includes('buy')) return 'cta';
    if (lower.includes('form') || lower.includes('input') || lower.includes('search')) return 'form';
    if (lower.includes('social') || lower.includes('share') || lower.includes('facebook') || lower.includes('twitter')) return 'social';
    return 'other';
  };

  const getUniquePages = (): string[] => {
    const pages = new Set(buttonClicks.map(click => click.page));
    return Array.from(pages).sort();
  };

  const getCategoryBreakdown = () => {
    const categoryStats = new Map<string, number>();
    const filteredClicks = selectedPage === 'all' 
      ? buttonClicks 
      : buttonClicks.filter(click => click.page === selectedPage);

    filteredClicks.forEach(click => {
      const category = guessCategory(click.element);
      categoryStats.set(category, (categoryStats.get(category) || 0) + click.clicks);
    });

    const totalClicks = filteredClicks.reduce((sum, c) => sum + c.clicks, 0);
    return Array.from(categoryStats.entries()).map(([category, count]) => ({
      category,
      count,
      percentage: totalClicks > 0 ? count / totalClicks : 0
    }));
  };

  if (loading && buttonClicks.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <p className="text-red-600">{error}</p>
        <button onClick={fetchButtonClicks} className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
          Retry
        </button>
      </div>
    );
  }

  const buttonStats = processButtonClickData();
  const categoryBreakdown = getCategoryBreakdown();
  const pages = getUniquePages();
  const totalClicks = buttonStats.reduce((sum, stat) => sum + stat.clicks, 0);

  const topButtonsChartData = buttonStats.slice(0, 8).map(stat => ({
    name: (stat.buttonText || stat.buttonId).substring(0, 15),
    clicks: stat.clicks
  }));

  const categoryChartData = categoryBreakdown.map(cat => ({
    name: cat.category ? cat.category.charAt(0).toUpperCase() + cat.category.slice(1) : 'Unknown',
    value: cat.count
  }));

  const getCategoryIcon = (category: string): string => {
    switch (category) {
      case 'navigation': return '🧭';
      case 'cta': return '🎯';
      case 'form': return '📝';
      case 'social': return '📱';
      default: return '🔘';
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h3 className="text-lg font-semibold text-gray-900">Button Click Analytics</h3>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Filter:</label>
            <select
              value={selectedPage}
              onChange={(e) => setSelectedPage(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Pages</option>
              {pages.map(page => (
                <option key={page} value={page}>{page}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="p-5">
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 rounded-xl p-4 text-center border border-blue-100">
            <div className="text-2xl md:text-3xl font-bold text-blue-600">{totalClicks}</div>
            <div className="text-sm text-gray-600">Total Clicks</div>
          </div>
          <div className="bg-green-50 rounded-xl p-4 text-center border border-green-100">
            <div className="text-2xl md:text-3xl font-bold text-green-600">{buttonStats.length}</div>
            <div className="text-sm text-gray-600">Unique Buttons</div>
          </div>
          <div className="bg-purple-50 rounded-xl p-4 text-center border border-purple-100">
            <div className="text-2xl md:text-3xl font-bold text-purple-600">
              {buttonStats.length > 0 ? (totalClicks / buttonStats.length).toFixed(1) : '0'}
            </div>
            <div className="text-sm text-gray-600">Avg Clicks</div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Most Clicked Buttons</h4>
            <div className="h-64">
              <BarChart
                data={topButtonsChartData}
                xAxisKey="name"
                bars={[{ key: 'clicks', color: '#3B82F6', name: 'Clicks' }]}
                height={240}
              />
            </div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Click Distribution by Category</h4>
            <div className="h-64">
              <PieChart
                data={categoryChartData}
                height={240}
                colors={['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6']}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700">Button Click Details</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Button</th>
                  <th className="px-4 py-3 text-left hidden md:table-cell">Page</th>
                  <th className="px-4 py-3 text-left hidden sm:table-cell">Category</th>
                  <th className="px-4 py-3 text-right">Clicks</th>
                  <th className="px-4 py-3 text-right">Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {buttonStats.slice(0, 10).map((stat, index) => {
                  const clickRate = totalClicks > 0 ? (stat.clicks / totalClicks) * 100 : 0;
                  return (
                    <tr key={`${stat.buttonId}-${index}`} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span>{getCategoryIcon(stat.category)}</span>
                          <span className="text-sm font-medium text-gray-900 truncate max-w-[150px]">
                            {stat.buttonText}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell truncate max-w-[120px]">
                        {stat.page}
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                          {stat.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                        {stat.clicks}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-right">
                        {clickRate.toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {categoryBreakdown.map((cat) => (
            <div key={cat.category} className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
              <div className="text-2xl mb-1">{getCategoryIcon(cat.category)}</div>
              <div className="text-xs font-medium text-gray-600 capitalize">{cat.category}</div>
              <div className="text-lg font-bold text-gray-900">{cat.count}</div>
              <div className="text-xs text-gray-500">{(cat.percentage * 100).toFixed(1)}%</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
